import { getPrimaryField, isFieldPrimary } from "@smartsuite/fields-logic/lib/utils/fields";
import type {
  AccountPlan,
  AppField,
  BaseRecord,
  GroupData,
  LinkedRecordFieldParams,
} from "@smartsuite/types";

import type { FormConfigApplicationStructureItem } from "../../../../../../types/form";

export const emptyGroupData: GroupData = {
  all_groups_collapsed: false,
  collapsed: {},
  group: [],
};

export const formStubAccountPlan: AccountPlan = {
  id: "plan_form_stub",
  category: "team",
  members_limit: null,
  number_of_solutions: null,
  allow_solutions_overage: false,
  records_per_solution: null,
  allow_records_overage: false,
  file_storage: null,
  allow_storage_overage: false,
  days_in_recycle_bin: 30,
  now_calculation_frequency_per_seconds: 60,
  allow_guest_users: false,
  features: {
    app_permissions: false,
    field_permissions: false,
    lock_view: false,
    view_folders: false,
    sso: false,
    mfa: false,
    ai: false,
    gantt: false,
    map: false,
    timeline: false,
    scripting: false,
    disable_smartsuite_branding: false,
    conditional_fields: false,
  },
  fields_limits: {
    max_fields_per_application: 200,
    max_linked_records_per_application: 20,
    max_lookups_per_application: 10,
    max_rollups_per_application: 10,
    max_counts_per_application: 10,
    max_formulas_per_application: 10,
    max_calculated_fields_per_application: 30,
    max_fields_per_sub_item: 100,
    max_linked_records_per_sub_item: 5,
  },
} as AccountPlan;

/** Drops duplicate slugs while preserving first-seen order (stable grid / React keys). */
export function dedupePreserveOrder(slugs: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const slug of slugs) {
    if (seen.has(slug)) continue;
    seen.add(slug);
    out.push(slug);
  }
  return out;
}

export function sortStructureByLabel(fields: AppField[]): AppField[] {
  return [...fields].sort((a, b) => (a.label || "").localeCompare(b.label || ""));
}

export function setPrimaryAsFirstField(fields: AppField[]): AppField[] {
  const primary = getPrimaryField(fields);
  if (!primary) {
    return fields;
  }
  const rest = fields.filter((field) => !isFieldPrimary(field));
  return [primary, ...rest];
}

export function mapFormConfigFieldToAppField(
  field: FormConfigApplicationStructureItem,
): AppField<LinkedRecordFieldParams> {
  return {
    ...field,
    params: field.params as LinkedRecordFieldParams,
  } as AppField<LinkedRecordFieldParams>;
}

export const isBacklinkRecord = (recordToCheck: BaseRecord): boolean =>
  !!recordToCheck.pre_save_id && !recordToCheck.id;
